import { useState } from "react";
import { useEntity, EditDialog, Field } from "./components/crud";
import { ScheduleEditor } from "./components/ScheduleEditor";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Pencil, Plus } from "lucide-react";

const BLANK = {
  name: "", slug: "", category_id: null, short_description: "", description_html: "", image: "",
  duration_min: 60, hide_duration: false, price: 0, min_spend: 0, deposit_amount: 0, deposit_enabled: false,
  buffer_before_min: 0, buffer_after_min: 0, active: true, visible: true, sort: 0, provider_ids: [],
  resource_group_id: null, min_ahead_minutes: null, max_ahead_days: null, booking_limit_per_guest: null,
  interval_minutes: 30, fixed_start_times: [], weekly_schedule: {}, date_overrides: [], notes_copy: "",
};

export default function ServicesPage() {
  const { items, save } = useEntity("services");
  const { items: categories } = useEntity("categories");
  const { items: providers } = useEntity("providers");
  const { items: groups } = useEntity("resource-groups");
  const [draft, setDraft] = useState(null);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  return (
    <div data-testid="admin-services-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Services</h1>
        <Button data-testid="add-service-btn" onClick={() => setDraft({ ...BLANK, sort: items.length + 1 })}>
          <Plus size={16} className="mr-1" /> Add service
        </Button>
      </div>
      <div className="bg-white border rounded-lg divide-y">
        {items.map((s) => (
          <div key={s.id} className="flex items-center gap-4 px-4 py-3">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{s.name}</p>
              <p className="text-xs text-muted-foreground">
                {catName(s.category_id)} · {s.duration_min} min ·{" "}
                {s.min_spend > 0 ? `min. spend €${s.min_spend}` : `€${s.price}`}
              </p>
            </div>
            {!s.active && <span className="text-xs px-2 py-0.5 rounded-full bg-stone-200 text-stone-600">Inactive</span>}
            {s.active && !s.visible && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Hidden</span>}
            {s.active && s.visible && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Live</span>}
            <Button variant="ghost" size="icon" data-testid={`edit-service-${s.slug}`} onClick={() => setDraft(s)}><Pencil size={14} /></Button>
          </div>
        ))}
      </div>

      {draft && (
        <EditDialog open title={draft.id ? "Edit service" : "New service"} onClose={() => setDraft(null)}
          onSave={async () => (await save(draft)) && setDraft(null)}>
          <Field label="Name"><Input data-testid="service-name-input" value={draft.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Slug"><Input value={draft.slug} onChange={(e) => set("slug", e.target.value)} /></Field>
            <Field label="Category">
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={draft.category_id || ""}
                onChange={(e) => set("category_id", e.target.value || null)}>
                <option value="">— none —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Short description"><Input value={draft.short_description} onChange={(e) => set("short_description", e.target.value)} /></Field>
          <Field label="Description (HTML allowed)"><Textarea rows={3} value={draft.description_html} onChange={(e) => set("description_html", e.target.value)} /></Field>
          <Field label="Image URL"><Input value={draft.image} onChange={(e) => set("image", e.target.value)} /></Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Duration (min)"><Input data-testid="service-duration-input" type="number" value={draft.duration_min} onChange={(e) => set("duration_min", Number(e.target.value))} /></Field>
            <Field label="Price €"><Input type="number" value={draft.price} onChange={(e) => set("price", Number(e.target.value))} /></Field>
            <Field label="Min. spend €"><Input type="number" value={draft.min_spend} onChange={(e) => set("min_spend", Number(e.target.value))} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Deposit €"><Input data-testid="service-deposit-amount" type="number" value={draft.deposit_amount} onChange={(e) => set("deposit_amount", Number(e.target.value))} /></Field>
            <Field label="Deposit on"><Switch data-testid="service-deposit-enabled" checked={draft.deposit_enabled} onCheckedChange={(v) => set("deposit_enabled", v)} /></Field>
            <Field label="Hide duration"><Switch checked={draft.hide_duration} onCheckedChange={(v) => set("hide_duration", v)} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Buffer before (min)"><Input type="number" value={draft.buffer_before_min} onChange={(e) => set("buffer_before_min", Number(e.target.value))} /></Field>
            <Field label="Buffer after (min)"><Input type="number" value={draft.buffer_after_min} onChange={(e) => set("buffer_after_min", Number(e.target.value))} /></Field>
            <Field label="Start interval (min)"><Input type="number" value={draft.interval_minutes} onChange={(e) => set("interval_minutes", Number(e.target.value))} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Provider">
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={draft.provider_ids?.[0] || ""}
                onChange={(e) => set("provider_ids", e.target.value ? [e.target.value] : [])}>
                <option value="">— any —</option>
                {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Resource group">
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={draft.resource_group_id || ""}
                onChange={(e) => set("resource_group_id", e.target.value || null)}>
                <option value="">— none —</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.units.length})</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Min ahead (min)"><Input type="number" value={draft.min_ahead_minutes ?? ""} placeholder="global"
              onChange={(e) => set("min_ahead_minutes", e.target.value === "" ? null : Number(e.target.value))} /></Field>
            <Field label="Max ahead (days)"><Input type="number" value={draft.max_ahead_days ?? ""} placeholder="global"
              onChange={(e) => set("max_ahead_days", e.target.value === "" ? null : Number(e.target.value))} /></Field>
            <Field label="Limit / guest"><Input type="number" value={draft.booking_limit_per_guest ?? ""} placeholder="none"
              onChange={(e) => set("booking_limit_per_guest", e.target.value === "" ? null : Number(e.target.value))} /></Field>
          </div>
          <Field label="Fixed start times (comma-separated, e.g. 10:00, 10:30)">
            <Input value={(draft.fixed_start_times || []).join(", ")}
              onChange={(e) => set("fixed_start_times", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
          </Field>
          <Field label="Guest notes (shown on booking)"><Textarea rows={3} value={draft.notes_copy} onChange={(e) => set("notes_copy", e.target.value)} /></Field>
          <Field label="Weekly schedule"><ScheduleEditor value={draft.weekly_schedule} onChange={(v) => set("weekly_schedule", v)} /></Field>
          <div className="flex items-center gap-8">
            <Field label="Active"><Switch data-testid="service-active-switch" checked={draft.active} onCheckedChange={(v) => set("active", v)} /></Field>
            <Field label="Visible"><Switch checked={draft.visible} onCheckedChange={(v) => set("visible", v)} /></Field>
            <Field label="Sort"><Input type="number" className="w-20" value={draft.sort} onChange={(e) => set("sort", Number(e.target.value))} /></Field>
          </div>
        </EditDialog>
      )}
    </div>
  );
}
